import React, { useState, useRef, useEffect } from 'react';
import { countries as countryList } from '../utils/countryUtils';

const countries = countryList.map(({ name, flag, isoCode }) => ({ name, flag, isoCode }));

const formatDisplayedValue = (value, valueType) => {
    if (!value) {
        return '';
    }
    if (valueType === 'isoCode') {
        return value.toUpperCase();
    }
    return value;
};

export default function CountryDropdown({ value, onChange, placeholder = "Select country", valueType = 'name', disabled = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState(formatDisplayedValue(value, valueType));
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownSearchRef = useRef(null);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
            setSearchTerm('');
        }
    }, [disabled]);

    const resolveSelectedCountry = () => {
        if (!value) {
            return undefined;
        }
        const target = value.toString().trim().toLowerCase();
        if (valueType === 'isoCode') {
            return countries.find(country => country.isoCode.toLowerCase() === target);
        }
        return countries.find(country => country.name.toLowerCase() === target);
    };

    const selectedCountry = resolveSelectedCountry();

    // Update input value when prop value changes
    useEffect(() => {
        setInputValue(formatDisplayedValue(value, valueType));
    }, [value, valueType]);

    // Filter countries based on search term
    const filteredCountries = countries.filter(country => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) {
            return true;
        }
        return country.name.toLowerCase().includes(normalizedSearch)
            || country.isoCode.toLowerCase().includes(normalizedSearch);
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus dropdown search input when dropdown opens
    useEffect(() => {
        if (isOpen && dropdownSearchRef.current) {
            dropdownSearchRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (country) => {
        if (disabled) {
            return;
        }
        const nextValue = valueType === 'isoCode' ? country.isoCode : country.name;
        setInputValue(formatDisplayedValue(nextValue, valueType));
        onChange(nextValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (disabled) {
            return;
        }
        if (valueType === 'isoCode') {
            onChange(newValue.toUpperCase());
        } else {
            onChange(newValue);
        }
    };

    const handleInputFocus = () => {
        // Don't open dropdown when focusing on input for typing
    };

    const handleDropdownToggle = (e) => {
        if (disabled) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        } else if (!disabled && e.key === 'ArrowDown' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
        }
    };

    const handleDropdownKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
            inputRef.current?.focus();
        }
    };

    return (
        <div className="country-dropdown" ref={dropdownRef}>
            <div className="country-input-container">
                {selectedCountry && (
                    <span className="country-flag">{selectedCountry.flag}</span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    className="country-input-field"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-disabled={disabled}
                />
                <button
                    type="button"
                    className="dropdown-toggle-button"
                    onClick={handleDropdownToggle}
                    tabIndex={-1}
                    disabled={disabled}
                    aria-disabled={disabled}
                >
                    <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
                </button>
            </div>

            {isOpen && (
                <div className="country-dropdown-list">
                    <input
                        ref={dropdownSearchRef}
                        type="text"
                        className="country-dropdown-search"
                        placeholder="Search countries..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleDropdownKeyDown}
                    />
                    {filteredCountries.length > 0 ? (
                        filteredCountries.map((country, index) => (
                            <div
                                key={`${country.name}-${index}`}
                                className="country-dropdown-item"
                                onClick={() => handleSelect(country)}
                            >
                                <span className="country-flag">{country.flag}</span>
                                <span className="country-name">
                                    {valueType === 'isoCode'
                                        ? `${country.isoCode} — ${country.name}`
                                        : country.name}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="country-dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                            No countries found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
} 
